import React, { useEffect, useMemo, useState } from 'react';

import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import { useDropzone } from 'react-dropzone';

import Spinner from '@atlaskit/spinner';

import { messages } from '@qality/common-messages';
import {
  JiraAttachment,
  TestExecutionStep,
  TestStep,
  User,
} from '@qality/common-model';
import {
  AsyncService,
  StorageOfUniqueDialogs,
  StorageOfUniqueModels,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

import AttachmentsThumb from './AttachmentsThumb';
import EnableAttachments from './EnableAttachments';
import TestCaseBoxAttachmentButton from './TestCaseBoxAttachmentButton';
import './_test-case-box-step.scss';

type Props = {
  testStep: TestStep | TestExecutionStep;
  onUpdateAttachment?: (testStep: TestStep | TestExecutionStep) => void;
  issueKey?: string;
  onLoading?: (value: boolean) => void;
  loading?: boolean;
  canDeleteAllAttachment?: boolean;
  canCreateAttachment?: boolean;
  canDeleteOwnAttachment?: boolean;
  currentUser?: User;
  canEdit: boolean;
  propertyNameToStoreAttachments?: string;
  pastedAttachments?: Array<File>;
  getShouldDeleteAttachment?: (
    fileId: number,
    testStep: TestStep | TestExecutionStep
  ) => boolean;
  shouldRefreshAttachments?: boolean;
  clearPastedAttachments?: () => void;
  isHovered?: boolean;
};

/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
export default function TestCaseBoxAttachments({
  testStep,
  onUpdateAttachment,
  issueKey,
  onLoading,
  loading,
  canDeleteAllAttachment,
  canCreateAttachment,
  canEdit,
  canDeleteOwnAttachment,
  currentUser,
  propertyNameToStoreAttachments = 'attachments',
  pastedAttachments,
  getShouldDeleteAttachment = () => true,
  shouldRefreshAttachments = false,
  clearPastedAttachments,
  isHovered,
}: Props): JSX.Element {
  const [files, setFiles] = useState([]);
  const [testStepAttachment, setTestStepAttachment] = useState(
    testStep[propertyNameToStoreAttachments]
  );

  useEffect(() => {
    async function asyncFetchAttachments() {
      const array = [];
      await AsyncService.asyncForEach(
        testStepAttachment,
        async (file: JiraAttachment) => {
          try {
            const response = await StorageOfUniqueServices.get(
              'AttachmentService'
            ).fetchAttachment(file.id);
            const parsed = JSON.parse(response.body);
            parsed.id = file.id;
            array.push(parsed);
          } catch (e) {
            if (e.xhr.status === 404) {
              return;
            }
            StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
              e,
              messages['error.testCaseBoxAttachments.fetchAttachment.message']
            );
          }
        }
      );
      setFiles(array);
    }
    asyncFetchAttachments();
  }, [testStepAttachment]);

  useEffect(() => {
    const areAttachmentsDifferent = () => {
      if (testStep.attachments.length !== testStepAttachment.length) {
        return true;
      }

      return testStep.attachments.some(
        (attachment, index) => attachment.id !== testStepAttachment[index]?.id
      );
    };

    if (shouldRefreshAttachments && areAttachmentsDifferent()) {
      setTestStepAttachment(testStep.attachments);
    }
  }, [testStep.attachments]);

  useEffect(() => {
    if (!Utils.isNilOrEmpty(pastedAttachments)) {
      uploadAttachmentsToTestStep(pastedAttachments);
      clearPastedAttachments?.();
    }
  }, [pastedAttachments]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: !canCreateAttachment && !canEdit,
    noDrag: !canCreateAttachment || !canEdit,
    onDrop: (acceptedFiles: Array<File>) => {
      uploadAttachmentsToTestStep(acceptedFiles);
    },
  });

  const style = useMemo(
    () => ({
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const removeAttachment = (fileId) => {
    if (getShouldDeleteAttachment(fileId, testStep)) {
      StorageOfUniqueServices.get('AttachmentService').deleteAttachment(fileId);
    }
    setFiles(() => {
      const newFiles = filter(files, (item) => item.id !== fileId);
      testStep[propertyNameToStoreAttachments] = newFiles;
      if (onUpdateAttachment) {
        onUpdateAttachment(testStep);
      }
      return newFiles;
    });
  };

  const enlargeAttachment = (index, isOpen) => {
    StorageOfUniqueDialogs.get('AddAttachmentDialog').openAttachmentDialog(
      index,
      files,
      isOpen
    );
  };

  const addAttachmentsToTestCase = (
    testCaseIssueKey: string,
    acceptedFiles: Array<File>
  ): Promise<Array<JiraAttachment>> => {
    if (onLoading && acceptedFiles != null) {
      onLoading(true);
    }

    return StorageOfUniqueServices.get('AttachmentService').postAttachments(
      testCaseIssueKey,
      acceptedFiles
    );
  };

  const bindAttachmentsToTestStep = (
    testCaseAttachments: Promise<Array<JiraAttachment>>
  ): Promise<boolean> => {
    return testCaseAttachments.then((attachmentList) => {
      const attachmentIdList = attachmentList.map((attachment) => {
        return {
          id: attachment.id,
          author: currentUser ? currentUser.getIdentifier() : null,
        };
      });
      testStep[propertyNameToStoreAttachments] = [
        ...attachmentIdList,
        ...testStepAttachment,
      ];
      if (onUpdateAttachment) {
        onUpdateAttachment(testStep);
      }
      setTestStepAttachment([...attachmentIdList, ...testStepAttachment]);
      setFiles([...attachmentList, ...files]);
      if (onLoading) {
        onLoading(false);
      }
      return true;
    });
  };

  const uploadAttachmentsToTestStep = (
    acceptedFiles: Array<File>
  ): Promise<boolean> => {
    if (issueKey) {
      const attachments = addAttachmentsToTestCase(issueKey, acceptedFiles);
      return bindAttachmentsToTestStep(attachments);
    }
  };

  const canDelete = (file) => {
    if (canDeleteAllAttachment) {
      return canEdit;
    }
    if (canDeleteOwnAttachment) {
      const UserModel = StorageOfUniqueModels.get('User');
      return currentUser
        ? new UserModel(file.author).getIdentifier() ===
            currentUser.getIdentifier()
        : false;
    }
    return false;
  };

  return (
    <div>
      {!StorageOfUniqueServices.get(
        'AttachmentConfigService'
      ).areAttachmentsEnabled() ? (
        <EnableAttachments />
      ) : (
        <div className='drop-container'>
          <div
            {...getRootProps({
              style,
              className: isEmpty(files)
                ? 'drop-container-empty'
                : 'drop-container-filled',
            })}
          >
            <input {...getInputProps()} />
            {files.map((file, index) => {
              return (
                <AttachmentsThumb
                  canDelete={canDelete(file)}
                  key={index}
                  file={file}
                  index={index}
                  removeAttachment={removeAttachment}
                  enlargeAttachment={enlargeAttachment}
                />
              );
            })}
            <TestCaseBoxAttachmentButton
              onClick={open}
              isHovered={isHovered}
              isVisible={canCreateAttachment && canEdit}
            />
          </div>
          {loading && (
            <div className='drop-container-spinner'>
              <Spinner size='medium' />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const activeStyle = {
  borderColor: 'var(--ds-border-focused, var(--ds-fb-border-focused))',
};

const acceptStyle = {
  borderColor: 'var(--ds-border-focused, var(--ds-fb-border-focused))',
};

const rejectStyle = {
  borderColor: 'var(--ds-border-danger, var(--ds-fb-border-danger))',
};
